import get from 'lodash/get';
import last from 'lodash/last';
import merge from 'lodash/merge';
import map from 'lodash/map';
import moment from '@utils/moment-wrapper';
import cloneDeep from 'lodash/cloneDeep';

import { EDITOR_TYPES } from '@pendo/services/Constants.js';
import sharedServicesPackage from '@pendo/services/package.json';
import { BuildingBlockLayouts, BuildingBlock, BuildingBlockEditing, StepCopy, SdkTools } from '@pendo/services/BuildingBlocks';

const { ROOT_MIN_SDK_VERSION } = SdkTools;

import { getIsMulti, RANK_INCREMENT } from './common.util.js';
import generateUniqueId from '@/utils/makeid';
import { getContentHost } from '@/settings/environment.utils.js';
import { AppsService as Apps } from '@/common/apps/apps.service';
import { get as gcsGet } from '@/data/gcs-service.js';
import { stepCssPrefix, combineContent } from '@/guides/helpers/guide-content';
import { getActiveApplicationsMap } from '@/data/session/session-globals.ts';
import { makeConvoUiStepKey } from '@/guides/helpers/templates/utils/ai-template-utils';

const { version: sharedServiceVersion } = sharedServicesPackage;

const iconInformationSymbol =
    'data:image/svg+xml;base64,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';

export const DEFAULT_GUIDE_SIZES = {
    banner: { height: 500, width: 500, autoHeight: true, position: 'top' },
    lightbox: { height: 500, width: 500, autoHeight: true },
    tooltip: { height: 230, width: 430, autoHeight: true },
    whatsnew: { height: 200, autoHeight: true },
};

const WIRE_GUIDE_ADVANCED_BUTTON = `
    (function wireGuideAdvanceButton (step) {
        step && step.attachEvent(step.guideElement[0], 'click', function (e) {
            var advanceButton = pendo.dom(e.target || e.srcElement).closest('._pendo-guide-next_');
            if (advanceButton.length) {
                pendo.onGuideAdvanced();
            }
        });
    })(step,guide);
`;

export async function createGuide ({
    guideType,
    app,
    maxPriority,
    skipPriority,
    editorType = EDITOR_TYPES.ENGAGE_UI,
    skipAddStep = false,
    isNPS = false,
    isMobile = false,
    isTraining,
    badge,
    confirmation,
    launchMethod,
    name = 'Unnamed',
}) {
    // Prior to this we had isMultiStep = type === 'walkthrough' || type === 'building-block'
    // This would incorrectly set isMultiStep to true even on single step p2 guides
    // Causing guide display bugs like:
    // https://pendo-io.atlassian.net/browse/APP-25825?oldIssueView=true
    const isMultiStep = guideType === 'walkthrough' || isNPS;
    const isBuildingBlockGuide = guideType === 'building-block';

    let guide = {};
    Object.assign(guide, {
        state: 'draft',
        isMultiStep,
        supportsBuildingBlocks: isBuildingBlockGuide,
        audienceUiHint: {
            filters: [],
        },
        name,
        launchMethod: launchMethod || (guideType === 'whatsnew' ? 'api' : 'auto'),
        attributes: {
            type: guideType,
            device: {
                type: 'desktop',
            },
            sharedServiceVersion,
        },
        steps: [],
        editorType,
        isTraining,
    });

    if (isMobile) {
        guide.minSdkVersion = ROOT_MIN_SDK_VERSION || '2.1.0.0';
    }

    const appId = get(app, 'id');
    if (appId) {
        guide.appId = appId;
    }

    if (!isBuildingBlockGuide) {
        guide.attributes.badge = {
            position: 'top-right',
            color: 'var(--palette-gray-70)',
            name: 'empty-i',
            offsets: { top: 0, right: 0, left: 0 },
        };
    }

    const { data } = await generateUniqueId();
    guide.id = data.id;
    guide.attributes.priority = (skipPriority ? -1 : maxPriority) + 1;

    const needsSteps = !skipAddStep && (!getIsMulti(guide) || guide.supportsBuildingBlocks);
    if (needsSteps && !isNPS) {
        guide = await addNewStepToGuide(guide);
    }

    if (badge) {
        const badgeConfig = {};
        if (confirmation) {
            badgeConfig.isConfirmation = true;
            badgeConfig.position = 'over';
            badgeConfig.showGuideOnBadgeHover = false;
        }

        return addBadgeToGuide(guide, badgeConfig);
    }

    return guide;
}

function addBadgeToGuide (guide, badgeConfig) {
    const { isConfirmation } = badgeConfig;
    const layoutType = isConfirmation ? 'badgeConfirmation' : 'badgeBlank';
    const layoutOptions = isConfirmation
        ? null
        : {
            src: iconInformationSymbol,
            type: 'image',
        };
    const badgeBuildingBlocks = BuildingBlockLayouts.createBaseBuildingBlocksForLayout(
        layoutType,
        guide.id,
        layoutOptions,
    );
    const badgeDom = BuildingBlock.buildingBlocksToDom(badgeBuildingBlocks);
    const defaultBadge = {
        bbJson: badgeBuildingBlocks,
        color: 'var(--palette-gray-100)',
        domJson: badgeDom,
        isOnlyShowOnce: false,
        position: 'inline',
        showBadgeOnlyOnElementHover: false,
        showGuideOnBadgeHover: false,
        isConfirmation: false,
    };

    const badgeProperties = {
        isMultiStep: false,
        launchMethod: 'badge',
        attributes: {
            badge: { ...defaultBadge, ...badgeConfig },
        },
    };

    return merge(guide, badgeProperties);
}

async function addNewStepToGuide (guide) {
    guide.steps = guide.steps || [];

    const step = await createBuildingBlockStep({ guide });
    guide.steps.push(step);

    return guide;
}

export async function addNewStepsToGuide ({ guide, buildingBlocks, themeId }) {
    if (!guide.supportsBuildingBlocks) return guide;
    guide.steps = guide.steps || [];

    const promises = [];
    buildingBlocks.forEach((blocks, index) => {
        const rank = RANK_INCREMENT * (index + 1);
        promises.push(createBuildingBlockStep({ guide, buildingBlocks: blocks, rank, themeId }));
    });

    const steps = await Promise.all(promises);
    guide.steps = guide.steps.concat(steps);

    return guide;
}

export async function createBuildingBlockStep ({ guide = undefined, buildingBlocks, rank, themeId }) {
    const step = {
        guideId: guide?.id,
        attributes: { themeId },
        templateId: 'building-block-guide',
        rank,
    };

    if (!rank && (!guide.steps || guide.steps.length === 0)) {
        step.rank = RANK_INCREMENT;
    } else if (!rank) {
        step.rank = last(guide.steps).rank + RANK_INCREMENT;
    }

    const { data: idData } = await generateUniqueId();
    const dom = buildingBlocks ? JSON.stringify(BuildingBlock.buildingBlocksToDom(buildingBlocks)) : undefined;

    return Object.assign(step, { id: idData.id, buildingBlocks, dom });
}

export async function createStep ({
    guide = {},
    type,
    template,
    previousSelection,
} = {}) {
    const step = {
        guideId: guide.id,
        type,
        advanceMethod: type === 'tooltip' ? 'element' : 'button',
        attributes: cloneDeep(DEFAULT_GUIDE_SIZES[type]) || {},
    };

    const activeAppsMap = getActiveApplicationsMap();
    const guideApp = activeAppsMap[guide.appId];
    const isMobileApp = Apps.isMobile(guideApp);

    if (isMobileApp) {
        step.activations = [{ event: 'appLaunch' }];
    }

    if (!guide.steps || guide.steps.length === 0) {
        step.rank = RANK_INCREMENT;
    } else {
        step.rank = last(guide.steps).rank + RANK_INCREMENT;
    }

    const { data: idData } = await generateUniqueId();
    const isExtension = Apps.isExtension(guideApp);
    let css = '';
    if (!isExtension) {
        css = template ? template.css : await getDefaultCss(type);
    }
    const contentHostUrl = getContentHost(guideApp);
    const content = await buildContent(guide, step, type, template, { id: idData.id, css, contentHostUrl });

    step.content = content;

    const selection = previousSelection || {};

    if (selection.target && selection.target.cssMatch) {
        step.elementPathRule = selection.target.cssMatch;
    }

    return step;
}

async function getDefaultCss (type) {
    if (type === 'building-block' || type === 'launcher') return '';

    const { status, data } = await gcsGet(
        `https://cdn.pendo.io/engage-app-ui/assets/classic-designer/templates/css/${type.toLowerCase()}.css`,
    );
    if (status === 404) return '';

    return data;
}

function buildContent (guide, step, type, template, defaults) {
    step.id = defaults.id;
    step.templateId = template && template.id;
    const { contentHostUrl } = defaults;

    Object.assign(step.attributes, template && template.layout);
    if (template && template.polls) {
        Object.assign(guide, {
            polls: (template && template.polls) || guide.polls || [],
        });
        Object.assign(step, {
            pollIds: map(guide.polls, 'id') || [],
        });
    }

    let html;
    const defaultStyles = '<!-- Default styles set in CSS tab -->';
    const headerFontSize = type === 'lightbox' ? 36 : 24;

    const { starterHtml, css } = buildP1TemplateAndCss({
        type,
        css: defaults.css,
        template,
        defaultStyles,
        headerFontSize,
    });

    if (template && template.variables) {
        template.variables.map((templateVar) => {
            if (templateVar.type === 'imageUpload') {
                const imageName = templateVar.default.name;

                if (contentHostUrl && !templateVar.oobe) {
                    return templateVar.default.url;
                }

                if (contentHostUrl || templateVar.oobe) {
                    templateVar.default.url = `https://${contentHostUrl}/img/${imageName}`;

                    return templateVar.default.url;
                }
            }

            return '';
        });
    }

    let templateHtml;
    if (template && template.html) {
        templateHtml = typeof template.html !== 'function' ? template.html : template.html(contentHostUrl);
    } else {
        templateHtml = starterHtml;
    }

    html = templateHtml;
    let javascript = template ? template.javascript : '';

    if (guide.isMultiStep && type === 'lightbox') {
        html += '\n<button class="_pendo-guide-next_">Next</button>';
        javascript += WIRE_GUIDE_ADVANCED_BUTTON;
    }

    step.attributes.css = css;

    return combineContent({
        html,
        javascript,
        css,
        cssPrefix: stepCssPrefix(step),
    });
}

function buildP1TemplateAndCss ({ type, css, template, defaultStyles, headerFontSize }) {
    let starterHtml;

    if (type === 'lightbox' || type === 'tooltip') {
        starterHtml = `
            ${defaultStyles}
            <p><span class="guide-header-text">Sample Header</span></p>
            <p><span>Sample subtext to elaborate on guide purpose.</span></p>
        `;

        if (!(template && template.html)) {
            css += `.guide-header-text { font-weight: bold; font-size: ${headerFontSize}px;}`;
        }
    } else if (type === 'banner') {
        starterHtml = `
            ${defaultStyles}
            <div><span class="banner-strong-text">Sample Banner Callout: </span>
            Sample subtext to elaborate on guide purpose.</div>`;

        if (css && !(template && template.html)) {
            css += '.banner-strong-text {font-weight: bold; font-size: 14px;}';
        }
    } else if (type === 'whatsnew') {
        starterHtml = `
            ${defaultStyles}\n
            <% if (pendo._.isFunction(pendo.guideWidget.formatDate)) { %>\n
            <h4 class="guide-date-text"><%= pendo.guideWidget.formatDate(guide.showsAfter || guide.publishedAt) || "<%= Published Date />" %></h4>\n
            <% } %>\n
            <h2 class="guide-header-text">Sample Announcement Header</h2>\n
            <p class="guide-description-text">Sample subtext to elaborate on the purpose of this guide announcement.</p>`;

        if (!(template && template.html)) {
            css +=
                'background-color: var(--palette-gray-20);\nborder-radius: 4px;\npadding: 15px;\n' +
                '.guide-date-text {\n    color: var(--palette-gray-60);\n    font-size: 12px;\n}\n' +
                '.guide-header-text {\n    color: var(--palette-gray-100);\n    font-size: 16px;\n    font-weight: bold;\n}\n' +
                '.guide-description-text {\n    color: var(--palette-gray-100);\n    font-size: 14px;\n}';
        }
    }

    return { starterHtml, css };
}

export function createdDate () {
    return moment().format('M/D [@] h:mm A');
}

export async function copyStepObject ({ guide = undefined, stepToCopy, theme, isMobile, isEmbed, rank }) {
    const { id: themeId } = theme;
    const baseStep = await createBuildingBlockStep({ guide, themeId, rank });
    const newStepId = baseStep.id;
    const containerId = get(BuildingBlock.findBlockByDomId(stepToCopy.buildingBlocks, 'pendo-g-'), 'web.domId');
    const { buildingBlocks, dom, otherProps } = StepCopy.copyStep(stepToCopy, newStepId, containerId, {}, true);

    if (isMobile) {
        delete otherProps.featureId;
        otherProps.trackTypeIds = [];
    }

    const newStep = {
        ...baseStep,
        ...otherProps,
        buildingBlocks,
        dom,
    };

    BuildingBlockEditing.appendEditingIdsToBuildingBlocks(newStep.buildingBlocks);

    if (theme) {
        BuildingBlock.applyThemeToBuildingBlocks({
            theme,
            buildingBlocks: newStep.buildingBlocks,
            options: { isEmbed },
        });
    }

    const rebuiltDom = BuildingBlock.buildingBlocksToDom(newStep.buildingBlocks);
    newStep.dom = JSON.stringify(rebuiltDom);

    return newStep;
}

export function initializeStepsForEditing (steps, theme = {}) {
    steps.forEach((step, index) => {
        const buildingBlocks =
            typeof step.buildingBlocks === 'string' ? JSON.parse(step.buildingBlocks) : step.buildingBlocks;

        BuildingBlockEditing.appendEditingIdsToBuildingBlocks(buildingBlocks);

        if (theme && Object.keys(theme).length) {
            BuildingBlock.applyThemeToBuildingBlocks({
                theme,
                buildingBlocks,
                options: { isEmbed: false },
            });
        }

        const dom = BuildingBlock.buildingBlocksToDom(buildingBlocks);

        step.buildingBlocks = buildingBlocks;
        step.dom = JSON.stringify(dom);
        step.key = makeConvoUiStepKey({ stepId: step.id, index, themeId: theme.id });
        step.rank = RANK_INCREMENT * (index + 1);

        return step;
    });
}
